/**
 * The widget protocol handshake (spec-langchain-native-runtime 08c; D-L65 as amended by D-L72).
 *
 * The widget has no socket `init` event and chat's link to socket-service is send-only, so the
 * widget tells chat which stream events it understands on the one chat-owned request it makes at
 * open: the history bootstrap `GET /chat-service/api/v1/conversations/all/:contactId`. Chat stores
 * the version for 24 h (`chat-service:widget:protocol:{tenantId}:{contactId}`, refreshed on every
 * bootstrap) and relays `trace` events only to sessions at ≥ 2.
 *
 * Chat records the header ONLY on a verified widget token, so the header rides only a request that
 * carries one (the caller attaches it next to `Authorization`). Nothing about the `/widgets/query`
 * body, the socket URL or any socket event changes for the handshake.
 */
import { WIDGET_PROTOCOL_VERSION } from "../constants/settings";
import { RENDERED_WIDGET_KINDS } from "../components/Messages/widgets/kinds";

export const WIDGET_PROTOCOL_HEADER = "x-widget-protocol";

/**
 * spec-widget-mcp 04/05 (WM04): the Tier-1 kinds this build renders, comma-separated, beside the
 * protocol on the same request. Chat remembers them with the protocol (24 h) and stamps them on every
 * turn as `widget_kinds`; a kind not listed is never composed (WM03) nor relayed (WM04).
 */
export const WIDGET_KINDS_HEADER = "x-widget-kinds";

const KIND_PATTERN = /^[a-z0-9_.-]{1,64}$/;

/** The header value chat accepts: each kind `^[a-z0-9_.-]{1,64}$`, at most 20, no duplicates. */
export const widgetKindsHeaderValue = (kinds = RENDERED_WIDGET_KINDS) =>
	[...new Set((Array.isArray(kinds) ? kinds : []).filter((k) => typeof k === "string" && KIND_PATTERN.test(k)))].slice(0, 20).join(",");

/** Chat ignores anything that is not an integer in 1–99 (08a) — never send such a value. */
export const isValidProtocolVersion = (version) => Number.isInteger(version) && version >= 1 && version <= 99;

const HISTORY_BOOTSTRAP_PATH = /^\/?conversations\/all\/[^/?#]+/;

/** Whether an axios request config is the history bootstrap (first load, pagination, reconnect). */
export const isHistoryBootstrap = (config) =>
	String(config?.method || "get").toLowerCase() === "get" && HISTORY_BOOTSTRAP_PATH.test(String(config?.url || ""));

/**
 * Add `x-widget-protocol: <version>` and `x-widget-kinds: <kinds>` (WM05) to a history-bootstrap
 * request config; any other request is returned untouched.
 * @param {object} config axios request config
 * @param {number} [version]
 * @param {string[]} [kinds] the rendered Tier-1 kinds
 * @returns {object} the same config
 */
export function withWidgetProtocol(config, version = WIDGET_PROTOCOL_VERSION, kinds = RENDERED_WIDGET_KINDS) {
	if (!config || !isHistoryBootstrap(config) || !isValidProtocolVersion(version)) return config;
	if (!config.headers) config.headers = {};
	config.headers[WIDGET_PROTOCOL_HEADER] = String(version);
	const kindsValue = widgetKindsHeaderValue(kinds);
	if (kindsValue) config.headers[WIDGET_KINDS_HEADER] = kindsValue;
	return config;
}
